import { useEffect } from "react";

interface PageMeta {
  title: string;
  description: string;
  path: string;
  image?: string;
}

const BASE_URL = "https://cipherandrow.com";
const DEFAULT_IMAGE = `${BASE_URL}/og/home.jpg`;

function setMeta(property: string, content: string) {
  const isOg = property.startsWith("og:") || property.startsWith("article:");
  const selector = isOg
    ? `meta[property="${property}"]`
    : `meta[name="${property}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    if (isOg) el.setAttribute("property", property);
    else el.setAttribute("name", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Sets document title + OG / Twitter meta tags for the current page.
 * On unmount, restores defaults so stale tags don't linger during SPA nav.
 */
export function usePageMeta({ title, description, path, image }: PageMeta) {
  useEffect(() => {
    const base = "Cipher & Row";
    const fullTitle = title ? `${title} | ${base}` : `${base} · Trust Infrastructure for Freight`;
    const fullUrl = `${BASE_URL}${path}`;
    const ogImage = image ?? DEFAULT_IMAGE;

    document.title = fullTitle;

    // Standard
    setMeta("description", description);

    // Open Graph
    setMeta("og:title", fullTitle);
    setMeta("og:description", description);
    setMeta("og:url", fullUrl);
    setMeta("og:image", ogImage);
    setMeta("og:image:width", "1200");
    setMeta("og:image:height", "630");
    setMeta("og:type", "website");
    setMeta("og:site_name", "Cipher & Row");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", fullUrl);

    return () => {
      document.title = `${base} · Trust Infrastructure for Freight`;
    };
  }, [title, description, path, image]);
}
